/**
 * <TutorAbsenceImpact> — ce que l'absence du masteur fera à chacun de ses élèves, AVANT qu'il la
 * confirme (molecule).
 *
 * L'ancien `AbsenceImpactComponent` : un bloc, seulement quand un élève change de masteur (sinon
 * l'écran reste celui d'avant, et la molécule ne rend rien) ; pour chaque élève, son prénom, le
 * pourquoi et une chip — « Change de masteur » ou « Remplacé » ; puis la phrase de fin. Les mots
 * sont ceux de l'ancien `absenceImpact.js` (`tutorAbsenceImpactText`).
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Divider } from "../../atoms/Divider"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../Card"
import { type UITutorAbsenceImpactStudent } from "../../../ports/primary/ui/types/tutorLives"
import {
    hasTutorChange,
    IMPACT_TITLE,
    impactDetails,
    impactStatusLabel,
    isTutorChange,
    TUTOR_CHANGE_CONSEQUENCE,
} from "./tutorAbsenceImpactText"

export type TutorAbsenceImpactProps = {
    students: UITutorAbsenceImpactStudent[] | null
    testID?: string
}

export function TutorAbsenceImpact(props: TutorAbsenceImpactProps) {
    const { students, testID } = props
    if (students == null || !hasTutorChange(students)) return null

    return (
        <Card testID={testID}>
            <Stack gap="3">
                <Text size="text-sm" weight="bold" color="text-muted">
                    {IMPACT_TITLE}
                </Text>
                {students.map((student) => {
                    const change = isTutorChange(student)
                    return (
                        <Row key={student.id} gap="2" align="center" justify="between">
                            <Stack gap="0" style={styles.grow}>
                                <Text weight="bold" color="text-primary">
                                    {student.firstname}
                                </Text>
                                {impactDetails(student).map((line) => (
                                    <Text key={line} size="text-xs" color="text-muted">
                                        {line}
                                    </Text>
                                ))}
                            </Stack>
                            <Badge
                                text={impactStatusLabel(student)}
                                variant={change ? "danger" : "neutral"}
                                tinted={change}
                                iconName={change ? "exchange-alt" : undefined}
                            />
                        </Row>
                    )
                })}
                <Divider />
                <Text size="text-sm" color="text-primary">
                    {TUTOR_CHANGE_CONSEQUENCE}
                </Text>
            </Stack>
        </Card>
    )
}

const styles = StyleSheet.create(() => ({
    grow: { flex: 1, flexShrink: 1 },
}))
